// Building programmes (V0.4.5): each revision is a building file (doc_type 'programme') carrying the dates Claude read
// or a PM typed. Milestones are matched across revisions by name (or by "was" when PBS renamed one) to show what moved,
// and the programme PC is checked against the contract PC plus the EOT days granted.
/** An activity under a milestone (V0.4.6): the tasks the programme lists for it. */
export type ProgItem = { name: string; start?: string | null; date: string | null; actual?: boolean; where?: string | null };
export type Milestone = { name: string; date: string | null; actual?: boolean; was?: string | null; where?: string | null; items?: ProgItem[] };
export type Programme = { rev: string | null; data_date: string | null; issued_by: string | null; pc_date: string | null; milestones: Milestone[] };
export type ProgFile = { id: string; original_name: string; created_at: string; programme: Programme | null };

const DAY = 86_400_000;
export const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
export const days = (a: string | null | undefined, b: string | null | undefined) =>
  a && b ? Math.round((new Date(b + 'T00:00:00Z').getTime() - new Date(a + 'T00:00:00Z').getTime()) / DAY) : null;
export const addDays = (iso: string, n: number) => {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + Math.round(n));
  return d.toISOString().slice(0, 10);
};
const isDate = (v: unknown): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

/** Clean a programme from Claude or a form: trimmed text, real dates, no blank milestones, at most 40. */
export function cleanProgramme(p: Partial<Programme> | null | undefined): Programme {
  const t = (v: unknown, n = 80) => (typeof v === 'string' && v.trim() ? v.trim().slice(0, n) : null);
  let budget = 600; // activities kept across all milestones
  const ms = (Array.isArray(p?.milestones) ? p!.milestones : [])
    .map((m) => ({ name: t(m?.name, 120) ?? '', date: isDate(m?.date) ? m.date : null, actual: Boolean(m?.actual), was: t(m?.was, 120), where: t(m?.where, 60), raw: m?.items }))
    .filter((m) => m.name)
    .slice(0, 60)
    .map(({ raw, ...m }) => {
      const items = (Array.isArray(raw) ? raw : [])
        .map((i) => ({ name: t(i?.name, 160) ?? '', start: isDate(i?.start) ? i.start : null, date: isDate(i?.date) ? i.date : null, actual: Boolean(i?.actual), where: t(i?.where, 60) }))
        .filter((i) => i.name)
        .slice(0, Math.max(0, Math.min(120, budget)));
      budget -= items.length;
      return items.length ? { ...m, items } : m;
    });
  return { rev: t(p?.rev, 40), data_date: isDate(p?.data_date) ? p!.data_date : null, issued_by: t(p?.issued_by), pc_date: isDate(p?.pc_date) ? p!.pc_date : null, milestones: ms };
}

/** Programme revisions newest first (data date, then upload date). */
export const byNewest = (a: ProgFile, b: ProgFile) =>
  String(b.programme?.data_date ?? b.created_at).localeCompare(String(a.programme?.data_date ?? a.created_at)) || b.created_at.localeCompare(a.created_at);

export type MoveState = 'later' | 'earlier' | 'same' | 'new' | 'done' | 'gone';
export type ItemMove = { name: string; start: string | null; prev: string | null; date: string | null; moved: number | null; state: MoveState };
export type MoveRow = { name: string; prev: string | null; date: string | null; moved: number | null; state: MoveState; actual: boolean; items: ItemMove[] };

/** Each milestone of `cur` against `prev`: moved later first, then new, earlier, unchanged, done; dropped ones last. */
export function compare(cur: Programme, prev: Programme | null): MoveRow[] {
  const pm = new Map((prev?.milestones ?? []).map((m) => [norm(m.name), m]));
  const used = new Set<string>();
  const rows: MoveRow[] = cur.milestones.map((m) => {
    const key = pm.has(norm(m.name)) ? norm(m.name) : m.was && pm.has(norm(m.was)) ? norm(m.was) : null;
    const p = key ? pm.get(key)! : null;
    if (key) used.add(key);
    const moved = p ? days(p.date, m.date) : null;
    const state: MoveRow['state'] = m.actual ? 'done' : !prev ? 'same' : !p ? 'new' : moved === null || moved === 0 ? 'same' : moved > 0 ? 'later' : 'earlier';
    return { name: m.name, prev: p?.date ?? null, date: m.date, moved, state, actual: Boolean(m.actual), items: compareItems(m.items ?? [], p?.items ?? [], Boolean(prev)) };
  });
  for (const [k, m] of pm) if (!used.has(k)) rows.push({ name: m.name, prev: m.date, date: null, moved: null, state: 'gone', actual: false, items: [] });
  const rank = { later: 0, new: 1, earlier: 2, same: 3, done: 4, gone: 5 };
  return rows.sort((a, b) => rank[a.state] - rank[b.state] || (b.moved ?? 0) - (a.moved ?? 0));
}

/** Activities under one milestone against the same milestone before (matched by name), in programme order. */
function compareItems(cur: ProgItem[], prev: ProgItem[], hasPrev: boolean): ItemMove[] {
  const pm = new Map(prev.map((i) => [norm(i.name), i]));
  return cur.map((i) => {
    const p = pm.get(norm(i.name)) ?? null;
    const moved = p ? days(p.date, i.date) : null;
    const state: MoveState = i.actual ? 'done' : !hasPrev ? 'same' : !p ? 'new' : moved === null || moved === 0 ? 'same' : moved > 0 ? 'later' : 'earlier';
    return { name: i.name, start: i.start ?? null, prev: p?.date ?? null, date: i.date, moved, state };
  });
}

/** Contract PC + granted EOT days, and how far the programme PC sits past it (positive = not covered). */
export function eotCheck(contractPc: string | null, grantedDays: number, programmePc: string | null) {
  const eotDate = contractPc ? addDays(contractPc, grantedDays) : null;
  const beyond = eotDate && programmePc ? days(eotDate, programmePc) : null;
  return { eotDate, beyond, onContract: days(contractPc, programmePc) };
}

export const signed = (n: number | null) => (n === null ? '—' : n > 0 ? `+${n} d` : n < 0 ? `−${-n} d` : '0 d');
